<script setup>
import * as d3 from 'd3';
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from 'vue';

import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import * as ContourDefinitions from '@/src/store/contours/contour-definitions';
import { useContourEventStore } from '@/src/store/contours/contour-event-store';
import { convertPointArrayToArray } from '@/src/store/contours/contour-processing/convertArrayToPointArray';

import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import {
  ANNOTATION_FONT,
  CONNECTOR_STROKE,
  CONTOUR_STROKE,
  ENDPOINT_RADIUS,
  POINT_RADIUS,
  THIN_STROKE,
} from './contour-draw-constants';
import {
  appendArrow,
  appendCircle,
  appendLabel,
  appendPath,
  appendSelectionHandle,
  applyGroupTransform,
} from './contour-svg-primitives';
import { coordinateTransformation } from './coordinateTransformation';
import {
  drawRangeCrossbars,
  getLocalizerDir2D,
  RANGE_CROSS_OPACITY,
  RANGE_CROSS_STROKE,
} from './lax-range-crossbar';

const props = defineProps({
  id: { type: String, required: true },
  active: { type: Boolean, required: true },
  currentImageUid: { type: String, required: true },
  showContours: { type: Boolean, required: true },
  canvasWidth: { type: Number, required: true },
  canvasHeight: { type: Number, required: true },
});

const emit = defineEmits(['updateContourFormat']);

// Getting stores:
const contourContainerStore = useContourContainerStore();
const viewportStateStore = useViewportStateStore();
const contourEventStore = useContourEventStore();
const currentStudyStore = useCurrentStudyStore();

// Used to get current viewer:
const {
  id: viewID,
  currentImageUid: curImageID,
  canvasWidth,
  canvasHeight,
  showContours,
} = toRefs(props);

const overlay = ref(null);
const formattedContourValues = ref('');

const visibleGeneratedContourIds = computed(() =>
  viewportStateStore.getVisibleGeneratedContourIds(viewID.value),
);

// This provides all of the contours on the current image. Used for drawing.
const currentDrawingContours = computed(() => {
  const container = contourContainerStore.getContourContainer(curImageID.value);
  if (!container) return null;

  // Trigger generation of any whitelisted generated contour IDs for this viewport!
  const whitelistedIds = visibleGeneratedContourIds.value;
  if (whitelistedIds && whitelistedIds.length > 0) {
    whitelistedIds.forEach((contourId) => {
      container.getContour(contourId);
    });
  }

  return container.getDrawingContours() || null;
});

const { scaleFactor, overlayTransform, imageToScreenMatrix } = coordinateTransformation({
  currentImageUid: curImageID,
  id: viewID,
  canvasWidth,
  canvasHeight,
});

const saxLvTransmuralityOffsetEndo = computed(() =>
  viewportStateStore.getSaxLvTransmuralityOffsetEndo(viewID.value),
);
const saxLvTransmuralityOffsetEpi = computed(() =>
  viewportStateStore.getSaxLvTransmuralityOffsetEpi(viewID.value),
);

function isDisplayedGeneratedContour(contourId) {
  const { label, id } = contourId;
  // exact (label + id) whitelist wins over label/offset rules
  if (isWhitelistedGeneratedId(label, id)) return true;
  return visibleGeneratedContours.value.includes(label);
}

const selectedContour = computed(() =>
  viewportStateStore.getSelectedContour(viewID.value),
);

const moveContour = computed(() =>
  viewportStateStore.getMoveContour(viewID.value),
);

const hideDrawingContour = computed(() =>
  viewportStateStore.getHideDrawingContour(viewID.value),
);

const hideDrawingContourLabel = computed(() =>
  viewportStateStore.getHideDrawingContourLabel(viewID.value),
);

const hiddenUserEditableContours = computed(() =>
  viewportStateStore.getHiddenUserEditableContours(viewID.value),
);

const visibleGeneratedContours = computed(() =>
  viewportStateStore.getVisibleGeneratedContours(viewID.value),
);

function isWhitelistedGeneratedId(label, id) {
  return visibleGeneratedContourIds.value.some(c =>
    ContourDefinitions.equalContourIds(c, { label, id }),
  );
}

const localizerDir2D = computed(() =>
  getLocalizerDir2D(viewID.value, curImageID.value, viewportStateStore, currentStudyStore),
);

function isSelected(contour) {
  return ContourDefinitions.equalContourIds(
    { label: contour.getProps().label, id: contour.getProps().id },
    selectedContour.value,
  );
}

function resolveColor(contour) {
  const { label, color } = contour.getProps();
  return ContourDefinitions.ContourLabelColor[label] || color;
}

function getContourDisplayOpacity(contour) {
  return contour.getProps().userEditable ? 1 : 0.99;
}

// `setMoveContour` stores the drag delta in image-pixel space, so it is applied
// directly here (no scaleFactor division). Returns whether this contour is the
// one being moved (used to brighten the centroid handle).
function moveDisplayContour(contourId, scaledContour, centroidPosition) {
  const mc = moveContour.value;
  if (
    mc
    && mc.label != null
    && ContourDefinitions.equalContourIds(
      { label: mc.label, id: mc.id },
      contourId,
    )
  ) {
    const dx = mc.distancePoint[0];
    const dy = mc.distancePoint[1];
    scaledContour.forEach((point) => {
      point[0] += dx;

      point[1] += dy;
    });
    if (centroidPosition) {
      centroidPosition.x += dx;

      centroidPosition.y += dy;
    }
    return true;
  }
  return false;
}

// --- per-type drawing ------------------------------------------------------

function drawClosedPolygon(group, contour, displayNumber = -1) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  const centroidPosition = contour.getCentroidScaled();
  const color = resolveColor(contour);
  const opacity = getContourDisplayOpacity(contour);

  const contourMoved = moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    centroidPosition,
  );

  appendPath(group, scaledContour, color, opacity, CONTOUR_STROKE);

  if (isSelected(contour)) {
    appendPath(
      group,
      scaledContour,
      d3.hsl(color).brighter(1),
      1,
      CONTOUR_STROKE,
    );
    appendSelectionHandle(group, centroidPosition, color, contourMoved);
  }

  if (displayNumber > 0) {
    const firstPoint = scaledContour[0];
    appendLabel(group, firstPoint[0], firstPoint[1], displayNumber);
  }
}

function drawOpenPolygon(group, contour) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  const centroidPosition = contour.getCentroidScaled();
  const color = resolveColor(contour);
  const opacity = getContourDisplayOpacity(contour);

  const contourMoved = moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    centroidPosition,
  );

  appendPath(group, scaledContour, color, opacity, CONTOUR_STROKE);

  if (
    contour.getProps().label !== ContourDefinitions.ContourLabel.SaxLvOpenEpi
  ) {
    const straightLine = [
      scaledContour[0],
      scaledContour[scaledContour.length - 1],
    ];
    appendPath(group, straightLine, color, 0.4, THIN_STROKE);
  }

  if (isSelected(contour)) {
    appendPath(
      group,
      scaledContour,
      d3.hsl(color).brighter(1),
      1,
      CONTOUR_STROKE,
    );
    appendSelectionHandle(group, centroidPosition, color, contourMoved);
  }

  appendCircle(
    group,
    scaledContour[0][0],
    scaledContour[0][1],
    ENDPOINT_RADIUS,
    {
      fill: color,
      opacity,
    },
  );
  appendCircle(
    group,
    scaledContour[scaledContour.length - 1][0],
    scaledContour[scaledContour.length - 1][1],
    ENDPOINT_RADIUS,
    { fill: color, opacity },
  );
}

function drawLine(group, contour, displayNumber = -1) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  const centroidPosition = contour.getCentroidScaled();
  const color = resolveColor(contour);
  const opacity = getContourDisplayOpacity(contour);
  const { userEditable } = contour.getProps();

  const contourMoved = moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    centroidPosition,
  );

  appendPath(group, scaledContour, color, opacity, CONTOUR_STROKE);

  if (contour.getProps().label === ContourDefinitions.ContourLabel.LaxLvRange) {
    drawRangeCrossbars(group, scaledContour, localizerDir2D.value, {
      color,
      opacity: RANGE_CROSS_OPACITY,
      strokeWidth: RANGE_CROSS_STROKE,
      nonScalingStroke: true,
    });
  }

  // Endpoint handles only for editable lines (generated chords stay clean).
  if (userEditable && scaledContour.length >= 2) {
    appendCircle(
      group,
      scaledContour[0][0],
      scaledContour[0][1],
      ENDPOINT_RADIUS,
      {
        fill: color,
        opacity,
      },
    );
    appendCircle(
      group,
      scaledContour[scaledContour.length - 1][0],
      scaledContour[scaledContour.length - 1][1],
      ENDPOINT_RADIUS,
      { fill: color, opacity },
    );
  }

  if (isSelected(contour)) {
    appendPath(
      group,
      scaledContour,
      d3.hsl(color).brighter(1),
      1,
      CONTOUR_STROKE,
    );
    appendSelectionHandle(group, centroidPosition, color, contourMoved);
  }

  if (displayNumber > 0) {
    const firstPoint = scaledContour[0];
    appendLabel(group, firstPoint[0], firstPoint[1], displayNumber, 8);
  }
}

function drawPoint(group, contour) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  if (!scaledContour || !scaledContour.length) return;

  const color = resolveColor(contour);
  moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    null,
  );

  if (
    contour.getProps().label
    === ContourDefinitions.ContourLabel.LaxLvEndoBasalPoint
  ) {
    const endoContourId = {
      label: ContourDefinitions.ContourLabel.LaxLvEndo,
      id: ContourDefinitions.encodeContourLabelItemId([0]),
    };
    if (
      contourContainerStore.isPresentContourContourId(
        curImageID.value,
        endoContourId,
      )
    ) {
      const endoContour = convertPointArrayToArray(
        contourContainerStore.getContour(curImageID.value, endoContourId),
      );
      const endPoint1 = endoContour[0];
      const endPoint2 = endoContour[endoContour.length - 1];

      const mc = moveContour.value;
      if (mc && mc.label === ContourDefinitions.ContourLabel.LaxLvEndo) {
        endPoint1[0] += mc.distancePoint[0];
        endPoint1[1] += mc.distancePoint[1];
        endPoint2[0] += mc.distancePoint[0];
        endPoint2[1] += mc.distancePoint[1];
      }

      appendPath(
        group,
        [endPoint1, scaledContour[0]],
        color,
        1,
        CONNECTOR_STROKE,
      );
      appendPath(
        group,
        [endPoint2, scaledContour[0]],
        color,
        1,
        CONNECTOR_STROKE,
      );
    }
  }

  appendCircle(group, scaledContour[0][0], scaledContour[0][1], POINT_RADIUS, {
    fill: color,
    stroke: 'white',
    strokeWidth: 1,
  });

  if (isSelected(contour)) {
    appendCircle(
      group,
      scaledContour[0][0],
      scaledContour[0][1],
      POINT_RADIUS + 2,
      {
        fill: 'none',
        stroke: 'white',
        strokeWidth: 1,
      },
    );
  }
}

function drawArrow(group, contour) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  if (scaledContour.length < 2) return;
  const centroidPosition = contour.getCentroidScaled();
  const color = resolveColor(contour);
  const opacity = getContourDisplayOpacity(contour);

  const contourMoved = moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    centroidPosition,
  );

  const start = scaledContour[0];
  const end = scaledContour[1];
  const head = appendArrow(group, start, end, color, opacity);
  if (!head) return;

  if (isSelected(contour)) {
    appendPath(
      group,
      [start, head.shaftEnd],
      d3.hsl(color).brighter(1),
      1,
      CONTOUR_STROKE,
    );
    appendSelectionHandle(group, centroidPosition, color, contourMoved);
  }
}

function drawText(group, contour) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  if (!scaledContour || !scaledContour.length) return;
  const { text } = contour.getProps();
  if (!text) return;
  moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    null,
  );
  const [x, y] = scaledContour[0];
  const color = resolveColor(contour);
  const selected = isSelected(contour);
  const textEl = group
    .append('text')
    .attr('x', x)
    .attr('y', y)
    .attr('fill', selected ? 'yellow' : color)
    .attr('font-weight', selected ? 'bold' : 'normal')
    .attr('original-font-size', ANNOTATION_FONT)
    .attr('font-size', `${ANNOTATION_FONT}px`)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'middle')
    .text(text);
  if (selected) {
    textEl
      .attr('stroke', 'white')
      .attr('stroke-width', 0.75)
      .attr('vector-effect', 'non-scaling-stroke')
      .attr('paint-order', 'stroke'); // outline sits behind the glyph fill
  }
}

function drawByType(group, contour, displayNumber = -1) {
  switch (contour.getProps().type) {
    case ContourDefinitions.ContourType.PolygonClosedContour:
      drawClosedPolygon(group, contour, displayNumber);
      break;
    case ContourDefinitions.ContourType.PolygonOpenContour:
      drawOpenPolygon(group, contour);
      break;
    case ContourDefinitions.ContourType.LineContour:
      drawLine(group, contour, displayNumber);
      break;
    case ContourDefinitions.ContourType.PointContour:
      drawPoint(group, contour);
      break;
    case ContourDefinitions.ContourType.AnnotationArrowContour:
      drawArrow(group, contour);
      break;
    case ContourDefinitions.ContourType.AnnotationTextContour:
      drawText(group, contour);
      break;
    default:
      break;
  }
}

// --- value strings (emitted to the parent annotation overlay) --------------

function buildPolygonValueLine(contour, displayNumber) {
  const areaRW = Math.round(contour.getAreaRealWorldvalue()) / 100;
  const siStat = contour.getSignalIntensityStatistic();
  return (
    `${displayNumber}. ${areaRW} cm²`
    + ` ${Math.round(siStat.mean * 10) / 10}±${Math.round(siStat.stdv * 10) / 10
    }`
    + ` median ${Math.round(siStat.median * 10) / 10}`
    + ` min/max ${Math.round(siStat.min)}/${Math.round(siStat.max)}`
  );
}

function buildLineValueLine(contour, displayNumber) {
  const distanceRW = Math.round(contour.getDistanceRealWorldvalue() * 10) / 10;
  return `${displayNumber}. ${distanceRW} mm`;
}

// --- main render -----------------------------------------------------------

function createOverlay() {
  const root = d3.select(overlay.value);
  if (root.empty() || !root.node()) return;

  formattedContourValues.value = '';
  emit('updateContourFormat', '');
  root.selectAll('svg').remove();

  const contours = currentDrawingContours.value;
  if (!contours) return;

  const svg = root
    .append('svg')
    .attr('width', '100%')
    .attr('height', '100%')
    .style('opacity', showContours.value ? 1 : 0);
  // All contour geometry lives in this group in image-pixel coordinates; the
  // group carries the (rotation-aware) image->screen matrix in applyTransform.
  const group = svg.append('g').attr('class', 'contour-root');

  const visibleGenerated = visibleGeneratedContours.value;

  // 1) generated (non-editable) contours first, so editable ones draw on top.

  for (const [label, ids] of Object.entries(contours)) {
    // SaxLvChord: draw every segment as a plain line when the label is visible.
    if (label === ContourDefinitions.ContourLabel.SaxLvChord) {
      if (visibleGenerated.includes(label)) {
        for (const contour of Object.values(ids)) {
          if (
            contour.getProps().type
            === ContourDefinitions.ContourType.LineContour
          ) {
            drawLine(group, contour);
          }
        }
      }

      continue;
    }

    for (const [id, contour] of Object.entries(ids)) {
      if (
        contour.getProps().userEditable
        || !isDisplayedGeneratedContour({ label, id })
      ) {
        continue;
      }
      drawByType(group, contour);
    }
  }

  // 2) editable contours + value strings.
  let displayValueCounter = 1;
  let displayValueString = '';

  for (const [label, ids] of Object.entries(contours)) {
    for (const [id, contour] of Object.entries(ids)) {
      if (!contour.getProps().userEditable) {
        continue;
      }
      if (
        hideDrawingContour.value
        && hideDrawingContour.value.label === label
        && hideDrawingContour.value.id === id
      ) {
        continue;
      }
      if (hiddenUserEditableContours.value.includes(label)) {
        continue;
      }
      if (
        hideDrawingContourLabel.value
        && hideDrawingContourLabel.value === label
      ) {
        continue;
      }

      let displayNumber = -1;
      if (label === ContourDefinitions.ContourLabel.GeneralPolygon) {
        displayNumber = displayValueCounter;
        displayValueCounter += 1;
        displayValueString += `${buildPolygonValueLine(
          contour,
          displayNumber,
        )}\n`;
      }
      if (label === ContourDefinitions.ContourLabel.GeneralLine) {
        displayNumber = displayValueCounter;
        displayValueCounter += 1;
        displayValueString += `${buildLineValueLine(contour, displayNumber)}\n`;
      }

      drawByType(group, contour, displayNumber);
    }
  }

  formattedContourValues.value = displayValueString;
  emit('updateContourFormat', formattedContourValues.value);

  applyTransform();
}

// Applies the image->screen affine to the contour group and keeps radii/fonts
// at constant on-screen size. Strokes use `non-scaling-stroke`, so they are
// already constant and need no counter-scaling here.
function applyTransform() {
  const group = d3.select(overlay.value).select('g.contour-root');
  applyGroupTransform(group, {
    overlayTransform: overlayTransform.value,
    scaleFactor: scaleFactor.value,
    imageToScreenMatrix: imageToScreenMatrix.value,
  });
}

// --- lifecycle + reactivity ------------------------------------------------

let unsubscribeContourChange = null;

onMounted(() => {
  // Redraw only when the change touches the image shown in this view
  // (batch/deleteAll events carry no UIDs -> always redraw).
  unsubscribeContourChange = contourEventStore.on((event) => {
    if (event.UIDs.length && !event.UIDs.includes(curImageID.value)) return;
    createOverlay();
  });
  createOverlay();
});

onUnmounted(() => {
  if (unsubscribeContourChange) {
    unsubscribeContourChange();
    unsubscribeContourChange = null;
  }
});

// Camera pan/zoom/rotate: only re-apply the transform (cheap, no rebuild).
watch(
  () => overlayTransform.value,
  () => requestAnimationFrame(applyTransform),
);

watch(() => visibleGeneratedContours.value, createOverlay, { deep: false });
watch(() => visibleGeneratedContourIds.value, createOverlay, { deep: false });
watch(() => hiddenUserEditableContours.value, createOverlay, { deep: false });
watch(() => selectedContour.value, createOverlay, { deep: false });
watch(() => moveContour.value, createOverlay, { deep: false });
watch(() => saxLvTransmuralityOffsetEndo.value, createOverlay, { deep: false });
watch(() => saxLvTransmuralityOffsetEpi.value, createOverlay, { deep: false });
watch(() => curImageID.value, createOverlay, { deep: false });
watch(
  () => showContours.value,
  () => {
    d3.select(overlay.value)
      .selectAll('svg')
      .transition()
      .duration(300)
      .style('opacity', showContours.value ? 1 : 0);
  },
);
watch(() => localizerDir2D.value, createOverlay, { deep: false });
</script>

<script>
export default {
  name: 'UIDDisplayContours',
};
</script>

<template>
  <div class="overlay">
    <div
      ref="overlay" style="
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        will-change: transform;
      "
    />
  </div>
</template>

<style scoped>
.overlay {
  position: absolute;
  pointer-events: none;
  z-index: 0;
  width: 100%;
  height: 100%;
}
</style>
